import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiChevronRight, FiPlus, FiRefreshCw, FiX } from 'react-icons/fi';
import { ROUTES } from '../../../app/routes';
import { useSubmissionCenterStats } from '../../../context/SubmissionCenterStatsContext';
import { useToast } from '../../../context/ToastContext';
import { createOpportunity, fetchOpportunityStages, fetchOpportunityInsights, insightStatusTone, type OpportunityInsights } from './submissionApi';
import AqSelect from './AqSelect';
import '../../../styles/AddQuotePage.css';
import '../../../styles/SubmissionCenter.css';
import '../../../styles/CarrierDownload.css';
import '../../../styles/MomentumCommand.css';
import '../../../styles/Artifacts.css';

const AUTH_URL = process.env.REACT_APP_Auth_Url || '';
// Same base OpportunitiesSubmission (steps/tracking) endpoints live on — matches
// ACCOUNT_AMP_BASE in SubmissionCenterSidebarContent.tsx.
const ACCOUNT_AMP_BASE = process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com';
const PAGE_SIZE = 10;
// Quotes-per-opportunity "Show more" page size — matches the 5-at-a-time
// convention Notes/Subjectivities already use in QuotePolicyDetailPanel.
const QUOTES_PAGE_SIZE = 5;

type DatePreset = 'today' | 'week' | 'month';
type SortKey = 'effective' | 'account';

/**
 * Submission Center — live opportunities list.
 *
 * Backed by MomentumAMPAPI's /api/AMPNowcerts/opportunities, which itself proxies
 * NowCerts' OpportunitiesList OData feed (filtered by createDate, paginated
 * server-side). NowCerts' opportunity records don't carry the per-carrier
 * submissions/health/SLA/automations concepts the previous sample-data mockup
 * showed — this is deliberately the flat live list instead.
 */

interface LiveOpportunity {
  databaseId?: string;
  insuredDatabaseId?: string;
  insuredCommercialName?: string;
  insuredFirstName?: string;
  insuredLastName?: string;
  lineOfBusinessName?: string;
  lineOfBusinesses?: Array<{ lineOfBusinessName?: string }>;
  opportunityStageName?: string;
  stage?: string;
  createDate?: string;
  policyNumbers?: string[];
  assignedTo?: string[];
  winProbability?: string;
  description?: string;
  agencyCommission?: number;
  neededBy?: string;
  insuredEmail?: string;
  createdFromRenewal?: boolean;
  [key: string]: unknown;
}

interface OpportunitiesResponse {
  opportunities: LiveOpportunity[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  window?: { start: string; end: string };
}

function insuredName(o: LiveOpportunity): string {
  if (o.insuredCommercialName) return o.insuredCommercialName;
  const full = `${o.insuredFirstName || ''} ${o.insuredLastName || ''}`.trim();
  return full || '—';
}

function lineOfBusiness(o: LiveOpportunity): string {
  if (o.lineOfBusinessName) return o.lineOfBusinessName;
  if (Array.isArray(o.lineOfBusinesses) && o.lineOfBusinesses.length > 0) {
    return o.lineOfBusinesses[0]?.lineOfBusinessName || '—';
  }
  return '—';
}

function assignedToLabel(o: LiveOpportunity): string {
  return Array.isArray(o.assignedTo) ? o.assignedTo.filter(Boolean).join(', ') : '';
}

function stageLabel(o: LiveOpportunity): string {
  return o.opportunityStageName || o.stage || '—';
}

/** PolicyDetailList's isQuote: true means the underwriter has responded with terms
 *  (Received), false means we're still waiting on quote terms (Quoted). */
function quoteStatusLabel(isQuote: unknown): string {
  if (isQuote === true) return 'Received';
  if (isQuote === false) return 'Quoted';
  return '—';
}

function quoteStatusColor(isQuote: unknown): string | undefined {
  if (isQuote === false) return 'var(--accent-cyan)';
  if (isQuote === true) return 'var(--accent-orange)';
  return undefined;
}

function fmtCurrency(n: number): string {
  return `$${n.toLocaleString()}`;
}

/** The best quote for an opportunity is whichever fetched policy carries the
 *  lowest totalPremium — its number/carrier surface on the card, and that same
 *  premium figure doubles as the card's target premium. */
function bestQuotePolicy(policies: Array<Record<string, unknown>> | undefined): Record<string, unknown> | null {
  if (!policies || policies.length === 0) return null;
  let best: Record<string, unknown> | null = null;
  let bestPremium = Infinity;
  for (const p of policies) {
    if (typeof p.totalPremium === 'number' && p.totalPremium < bestPremium) {
      bestPremium = p.totalPremium;
      best = p;
    }
  }
  return best;
}

/** Maps NowCerts' free-text winProbability onto the tri-state health pill.
 *  NowCerts doesn't document a fixed enum for this field, so unrecognized
 *  values still render (just without a color implication). */
function winProbabilityTone(o: LiveOpportunity): 'on' | 'watch' | 'risk' | '' {
  const v = (o.winProbability || '').toLowerCase();
  if (!v) return '';
  if (/(excellent|good|high)/.test(v)) return 'on';
  if (/(fair|average|medium)/.test(v)) return 'watch';
  if (/(poor|low)/.test(v)) return 'risk';
  return '';
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const hasTz = /[Zz]|[+-]\d\d:\d\d$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function dateValue(iso?: string): number {
  if (!iso) return 0;
  const hasTz = /[Zz]|[+-]\d\d:\d\d$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** First-load placeholder — mirrors .sc-opp's box model (head / metrics / next
 *  row) so there's no layout shift once real cards replace it. */
const SkeletonCards: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="sc__list">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="sc__skel-card">
        <div className="sc__skel-head">
          <div className="sc__skel-bone" style={{ width: 14, height: 14, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="sc__skel-bone" style={{ width: `${45 - (i % 3) * 6}%`, height: 14 }} />
            <div className="sc__skel-bone" style={{ width: `${65 - (i % 4) * 8}%`, height: 10 }} />
          </div>
          <div className="sc__skel-bone" style={{ width: 70, height: 18, borderRadius: 99, flexShrink: 0 }} />
        </div>
        <div className="sc__skel-metrics">
          {[0, 1, 2].map((j) => (
            <div key={j} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="sc__skel-bone" style={{ width: '50%', height: 9 }} />
              <div className="sc__skel-bone" style={{ width: '70%', height: 14 }} />
            </div>
          ))}
        </div>
        <div className="sc__skel-next">
          <div className="sc__skel-bone" style={{ width: `${55 - (i % 3) * 10}%`, height: 12 }} />
        </div>
      </div>
    ))}
  </div>
);

/** First-load placeholder for the expanded quotes table — mirrors its
 *  4-column grid so there's no layout shift once real rows replace it. */
const SkeletonQuoteRows: React.FC<{ count?: number }> = ({ count = 2 }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="sc__skel-quote-row">
        <div className="sc__skel-bone" style={{ width: `${70 - (i % 2) * 15}%`, height: 12 }} />
        <div className="sc__skel-bone" style={{ width: `${60 - (i % 2) * 10}%`, height: 12 }} />
        <div className="sc__skel-bone" style={{ width: '50%', height: 12 }} />
        <div className="sc__skel-bone" style={{ width: '40%', height: 12 }} />
      </div>
    ))}
  </>
);

/** First-load placeholder for the expanded "Submission Steps" checklist —
 *  mirrors the .sc-panel__list checkbox-row shape (icon + title line + meta
 *  line) so there's no layout shift once real steps replace it. */
const SkeletonSteps: React.FC<{ count?: number }> = ({ count = 2 }) => (
  <ul className="sc-panel__list" style={{ marginTop: 8 }}>
    {Array.from({ length: count }, (_, i) => (
      <li key={i}>
        <span className="sc-panel__list-icon" style={{ paddingTop: 0 }}>
          <input type="checkbox" className="sc-panel__list-checkbox" disabled />
        </span>
        <span className="sc-panel__list-main">
          <div className="sc__skel-bone" style={{ width: `${55 - (i % 2) * 10}%`, height: 12 }} />
          <div className="sc__skel-bone" style={{ width: `${75 - (i % 2) * 8}%`, height: 10, marginTop: 5 }} />
        </span>
      </li>
    ))}
  </ul>
);

const SubmissionCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const { setSelected, quoteAddedSignal, lastQuoteAdded } = useSubmissionCenterStats();
  const { showSuccess, showError } = useToast();
  const [opportunities, setOpportunities] = useState<LiveOpportunity[]>([]);
  const [stageOptions, setStageOptions] = useState<string[]>([]);
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [savingStageId, setSavingStageId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchOpportunityStages()
      .then((list) => { if (!cancelled) setStageOptions(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Defaults to the last 7 days (today-6 through today), same convention the
  // Dialer's own date filter uses for its "Last Week" preset.
  const [fromDate, setFromDate] = useState(() => {
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - 6);
    return toYMD(weekStart);
  });
  const [toDate, setToDate] = useState(() => toYMD(new Date()));
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const dateFilterMenuRef = useRef<HTMLDivElement>(null);

  const [lobFilter, setLobFilter] = useState('all');
  const [sort, setSort] = useState<SortKey>('effective');

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  interface QuotePoliciesState {
    loading: boolean;
    error: boolean;
    moreLoad: boolean;
    policies: Array<Record<string, unknown>>;
    total: number;
  }
  const [quotePolicies, setQuotePolicies] = useState<Record<string, QuotePoliciesState>>({});

  const fetchQuotePoliciesPage = useCallback((
    oppId: string, insuredDatabaseId: string, numbers: string[], skip: number,
  ): Promise<{ policies: Array<Record<string, unknown>>; total: number }> => {
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken = localStorage.getItem('amsToken') || '';
    const params = new URLSearchParams({ insuredDatabaseId, skip: String(skip), top: String(QUOTES_PAGE_SIZE) });
    numbers.forEach((n) => params.append('numbers', n));
    return fetch(`${AUTH_URL}/api/AMPNowcerts/opportunities/quotes-policies?${params.toString()}`, {
      headers: { Authorization: `Bearer ${authToken}`, NCToken: amsToken },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => ({
        policies: Array.isArray(data?.policies) ? data.policies : [],
        total: typeof data?.total === 'number' ? data.total : (Array.isArray(data?.policies) ? data.policies.length : 0),
      }));
  }, []);

  const fetchQuotePolicies = useCallback((oppId: string, insuredDatabaseId: string, numbers: string[]) => {
    setQuotePolicies((prev) => ({ ...prev, [oppId]: { loading: true, error: false, moreLoad: false, policies: [], total: 0 } }));
    fetchQuotePoliciesPage(oppId, insuredDatabaseId, numbers, 0)
      .then(({ policies, total }) => {
        setQuotePolicies((prev) => ({ ...prev, [oppId]: { loading: false, error: false, moreLoad: false, policies, total } }));
      })
      .catch(() => {
        setQuotePolicies((prev) => ({ ...prev, [oppId]: { loading: false, error: true, moreLoad: false, policies: [], total: 0 } }));
      });
  }, [fetchQuotePoliciesPage]);

  const handleLoadMoreQuotePolicies = useCallback((oppId: string, insuredDatabaseId: string, numbers: string[]) => {
    setQuotePolicies((prev) => {
      const current = prev[oppId];
      if (!current || current.moreLoad || current.policies.length >= current.total) return prev;
      return { ...prev, [oppId]: { ...current, moreLoad: true } };
    });
    fetchQuotePoliciesPage(oppId, insuredDatabaseId, numbers, quotePolicies[oppId]?.policies.length || 0)
      .then(({ policies, total }) => {
        setQuotePolicies((prev) => {
          const current = prev[oppId];
          if (!current) return prev;
          return { ...prev, [oppId]: { ...current, moreLoad: false, policies: [...current.policies, ...policies], total } };
        });
      })
      .catch(() => {
        setQuotePolicies((prev) => {
          const current = prev[oppId];
          if (!current) return prev;
          return { ...prev, [oppId]: { ...current, moreLoad: false } };
        });
      });
  }, [fetchQuotePoliciesPage, quotePolicies]);

  // Submission tracking steps for this opportunity's line of business — the
  // name-lookup counterpart to GET /api/OpportunitiesSubmission/get (which the
  // Submission Center sidebar's Configuration tab calls by id) — this one's
  // by lineOfBusinessName, since an opportunity row here only carries the
  // LOB's name, not its id.
  interface SubmissionStepsState {
    loading: boolean;
    error: boolean;
    // `checked` and `hasCheckRecord` come from status-history: a step whose id
    // shows up in that response already has a check-history row on the
    // backend (hasCheckRecord: true), regardless of whether isCheck was true
    // or false. A step absent from that response has never been checked
    // before — its first check-uncheck call passes Id: 0 so the backend
    // inserts a new row instead of updating one that doesn't exist.
    steps: Array<{ id: number; title: string; description: string; checked?: boolean; hasCheckRecord?: boolean }>;
    // The LOB id get-by-name resolved server-side from lineOfBusinessName —
    // check-uncheck needs the id, not the name, and this is the one place
    // that id is available without a second lookup.
    lineOfBusinessId?: string;
  }
  const [submissionSteps, setSubmissionSteps] = useState<Record<string, SubmissionStepsState>>({});

  // Fires immediately on every checkbox click — POST
  // /api/OpportunitiesSubmission/check-uncheck, backed by a request model of
  // { Id, IsCheck, SelectedSteps, OpportunitiesId, LineOfBusinessId }.
  // Id is 0 the first time a step is checked; the response's id is then stored
  // and sent back on every subsequent toggle (check again, or uncheck) of that
  // same step. SelectedSteps carries just the one step being toggled right
  // now (as JSON), not every currently-checked step.
  const [togglingStep, setTogglingStep] = useState<string | null>(null);
  const handleToggleStepCheck = async (oppId: string, o: LiveOpportunity, stepId: number) => {
    const current = submissionSteps[oppId];
    const step = current?.steps.find((s) => s.id === stepId);
    if (!step) return;
    const key = `${oppId}:${stepId}`;
    if (togglingStep === key) return;
    const nextChecked = !step.checked;
    // Only the one step being toggled right now — not the whole checked set.
    const selectedSteps = JSON.stringify([{ id: step.id, title: step.title, description: step.description }]);
    // Resolved by get-by-name when these steps were fetched, not looked up by
    // name again here — the id it hands back is the one check-uncheck needs.
    const lobId = current?.lineOfBusinessId || '';
    setTogglingStep(key);
    try {
      const res = await axios.post(
        `${ACCOUNT_AMP_BASE}/api/OpportunitiesSubmission/check-uncheck`,
        {
          Id: step.hasCheckRecord ? step.id : 0,
          IsCheck: nextChecked,
          SelectedSteps: selectedSteps,
          OpportunitiesId: oppId,
          LineOfBusinessId: lobId,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } },
      );
      showSuccess(res.data);
      // A row now definitely exists for this step either way (inserted just
      // now, or already existed and just got updated) — future toggles pass
      // this step's own id instead of 0.
      setSubmissionSteps((prev) => {
        const cur = prev[oppId];
        if (!cur) return prev;
        return {
          ...prev,
          [oppId]: {
            ...cur,
            steps: cur.steps.map((s) => (
              s.id === stepId
                ? { ...s, checked: nextChecked, hasCheckRecord: true }
                : s
            )),
          },
        };
      });
    } catch (err: any) {
      console.log('OpportunitiesSubmission/check-uncheck error:', err);
      showError(err?.response?.data || err);
    } finally {
      setTogglingStep(null);
    }
  };

  // Looks up each fetched step's checked/unchecked state — POST
  // /api/OpportunitiesSubmission/status-history with { Ids: <step ids> }.
  // The response's entries are matched back to steps by stepsId, and that
  // entry's isCheck becomes the step's initial `checked` value.
  // stepsLoading (the skeleton) stays true through this call too — it only
  // flips off once check status has actually been applied, so steps never
  // flash as unchecked before switching to their real state.
  const applyStepCheckStatus = useCallback((oppId: string, stepIds: number[]) => {
    if (!stepIds.length) {
      setSubmissionSteps((prev) => {
        const current = prev[oppId];
        return current ? { ...prev, [oppId]: { ...current, loading: false } } : prev;
      });
      return;
    }
    axios
      .post(
        `${ACCOUNT_AMP_BASE}/api/OpportunitiesSubmission/status-history`,
        { Ids: stepIds },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } },
      )
      .then((res) => {
        const list: any[] = Array.isArray(res.data) ? res.data : (res.data?.statusHistory || res.data?.data || []);
        const checkedById = new Map<number, boolean>();
        list.forEach((item) => {
          const stepsId = item?.stepsId ?? item?.StepsId;
          const isCheck = item?.isCheck ?? item?.IsCheck;
          if (stepsId != null) checkedById.set(stepsId, !!isCheck);
        });
        setSubmissionSteps((prev) => {
          const current = prev[oppId];
          if (!current) return prev;
          return {
            ...prev,
            [oppId]: {
              ...current,
              loading: false,
              // Presence in the response means a check-history row already
              // exists for that step, whether isCheck came back true or false.
              steps: current.steps.map((s) => (
                checkedById.has(s.id) ? { ...s, checked: checkedById.get(s.id), hasCheckRecord: true } : s
              )),
            },
          };
        });
      })
      .catch((err) => {
        console.log('OpportunitiesSubmission/status-history error:', err);
        setSubmissionSteps((prev) => {
          const current = prev[oppId];
          return current ? { ...prev, [oppId]: { ...current, loading: false } } : prev;
        });
      });
  }, []);

  const fetchSubmissionSteps = useCallback((oppId: string, lobName: string) => {
    setSubmissionSteps((prev) => ({ ...prev, [oppId]: { loading: true, error: false, steps: [] } }));
    axios
      .get(`${ACCOUNT_AMP_BASE}/api/OpportunitiesSubmission/get-by-name`, {
        params: { lineOfBusinessName: lobName },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
          NCToken: localStorage.getItem('amsToken') || '',
        },
      })
      .then((res) => {
        const steps = Array.isArray(res.data?.steps) ? res.data.steps : [];
        // get-by-name carries lineOfBusinessId on each step, not at the
        // response root — every step for a given LOB shares the same one.
        const lineOfBusinessId = steps[0]?.lineOfBusinessId ?? steps[0]?.LineOfBusinessId ?? undefined;
        // Still loading — status-history hasn't run yet, so checked/unchecked
        // isn't known. applyStepCheckStatus is the one that flips this false.
        setSubmissionSteps((prev) => ({ ...prev, [oppId]: { loading: true, error: false, steps, lineOfBusinessId } }));
        applyStepCheckStatus(oppId, steps.map((s: { id: number }) => s.id));
      })
      .catch((err) => {
        console.log('OpportunitiesSubmission/get (by name) error:', err);
        setSubmissionSteps((prev) => ({ ...prev, [oppId]: { loading: false, error: true, steps: [] } }));
      });
  }, [applyStepCheckStatus]);

  interface OpportunityInsightsState { loading: boolean; error: boolean; data: OpportunityInsights | null; }
  const [opportunityInsights, setOpportunityInsights] = useState<Record<string, OpportunityInsightsState>>({});

  const fetchOpportunityInsightsFor = useCallback((oppId: string, opportunityDatabaseId: string) => {
    setOpportunityInsights((prev) => ({ ...prev, [oppId]: { loading: true, error: false, data: null } }));
    fetchOpportunityInsights(opportunityDatabaseId)
      .then((data) => setOpportunityInsights((prev) => ({ ...prev, [oppId]: { loading: false, error: false, data } })))
      .catch(() => setOpportunityInsights((prev) => ({ ...prev, [oppId]: { loading: false, error: true, data: null } })));
  }, []);

  const toggleExpanded = (o: LiveOpportunity, id: string) => {
    const opening = !expanded.has(id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    if (opening) {
      const numbers = (Array.isArray(o.policyNumbers) ? o.policyNumbers : []).filter(Boolean);
      if (numbers.length > 0 && o.insuredDatabaseId && !quotePolicies[id]) {
        fetchQuotePolicies(id, o.insuredDatabaseId, numbers);
      }
      const lobName = lineOfBusiness(o);
      if (lobName && lobName !== '—' && !submissionSteps[id]) {
        fetchSubmissionSteps(id, lobName);
      }
      if (o.databaseId && !opportunityInsights[id]) {
        fetchOpportunityInsightsFor(id, o.databaseId);
      }
    }
  };

  // Zapier/InsertOpportunity is a full-record webhook, not a partial-update API —
  // updating just the stage still means resending everything the opportunity
  // already has, keyed off its real databaseId instead of the placeholder GUID
  // create-opportunity uses for brand-new records. Omitting a field here would
  // read to NowCerts as "clear this field," not "leave it alone."
  const handleStageChange = async (o: LiveOpportunity, oppId: string, newStage: string) => {
    if (!o.databaseId || newStage === stageLabel(o)) { setEditingStageId(null); return; }
    setSavingStageId(oppId);
    try {
      await createOpportunity({
        databaseId: o.databaseId,
        lineOfBusinessName: o.lineOfBusinessName || o.lineOfBusinesses?.[0]?.lineOfBusinessName || '',
        neededBy: o.neededBy,
        opportunityStageName: newStage,
        winProbability: o.winProbability,
        assignedTo: o.assignedTo,
        description: o.description,
        agencyCommission: o.agencyCommission,
        insuredDatabaseId: o.insuredDatabaseId,
        insuredEmail: o.insuredEmail,
        insuredFirstName: o.insuredFirstName,
        insuredLastName: o.insuredLastName,
        insuredCommercialName: o.insuredCommercialName,
        createdFromRenewal: o.createdFromRenewal,
        policyNumbers: Array.isArray(o.policyNumbers) ? o.policyNumbers : undefined,
      });
      setOpportunities((prev) => prev.map((item) => (
        item.databaseId === o.databaseId ? { ...item, opportunityStageName: newStage } : item
      )));
      setEditingStageId(null);
    } catch {
      // Left in edit mode (savingStageId still clears below) so the failed pick stays visible to retry.
    } finally {
      setSavingStageId(null);
    }
  };

  // Producer/CSR isn't in the live opportunity payload (NowCerts' OpportunitiesList
  // feed doesn't carry it), so that select still renders inert until a real field
  // is mapped in. winProbability doubles as "health" and is filterable below.
  const [healthFilter, setHealthFilter] = useState('all');

  const lobOptions = useMemo(() => {
    const set = new Set<string>();
    opportunities.forEach((o) => {
      const l = lineOfBusiness(o);
      if (l && l !== '—') set.add(l);
    });
    return Array.from(set).sort();
  }, [opportunities]);

  const healthOptions = useMemo(() => {
    const set = new Set<string>();
    opportunities.forEach((o) => { if (o.winProbability) set.add(o.winProbability); });
    return Array.from(set).sort();
  }, [opportunities]);

  const visibleOpportunities = useMemo(() => {
    let list = opportunities;
    if (lobFilter !== 'all') list = list.filter((o) => lineOfBusiness(o) === lobFilter);
    if (healthFilter !== 'all') list = list.filter((o) => o.winProbability === healthFilter);

    const sorted = list.slice();
    sorted.sort((a, b) => {
      if (sort === 'account') return insuredName(a).localeCompare(insuredName(b));
      return dateValue(a.createDate) - dateValue(b.createDate);
    });
    return sorted;
  }, [opportunities, lobFilter, healthFilter, sort]);

  const buildParams = (pageNumber: number, from: string, to: string) => {
    const params = new URLSearchParams({ page: String(pageNumber), pageSize: String(PAGE_SIZE) });
    if (from) params.set('start', from);
    if (to) params.set('end', to);
    return params;
  };

  // Always re-fetches page 1 — used for initial load, retry, and whenever the date
  // filter changes. Infinite-scroll pages beyond 1 are handled by loadMoreOpportunities.
  const fetchOpportunities = useCallback((from: string, to: string) => {
    setLoading(true);
    setError(false);
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken = localStorage.getItem('amsToken') || '';
    fetch(`${AUTH_URL}/api/AMPNowcerts/opportunities?${buildParams(1, from, to).toString()}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        NCToken: amsToken,
      },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: OpportunitiesResponse) => {
        const list = Array.isArray(data?.opportunities) ? data.opportunities : [];
        const totalCount = data?.total ?? 0;
        setOpportunities(list);
        setTotal(totalCount);
        setPage(1);
        setHasMore(list.length < totalCount);
      })
      .catch(() => {
        setOpportunities([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const loadMoreOpportunities = useCallback(() => {
    const nextPage = page + 1;
    setLoadingMore(true);
    const authToken = localStorage.getItem('authToken') || '';
    const amsToken = localStorage.getItem('amsToken') || '';
    fetch(`${AUTH_URL}/api/AMPNowcerts/opportunities?${buildParams(nextPage, fromDate, toDate).toString()}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        NCToken: amsToken,
      },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: OpportunitiesResponse) => {
        const list = Array.isArray(data?.opportunities) ? data.opportunities : [];
        const totalCount = data?.total ?? total;
        setOpportunities((prev) => [...prev, ...list]);
        setTotal(totalCount);
        setPage(nextPage);
        setHasMore(nextPage * PAGE_SIZE < totalCount);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [page, fromDate, toDate, total]);

  // Refreshing the whole opportunities list doesn't help here — the opportunity's
  // own policyNumbers field doesn't reflect a just-added quote reliably/immediately.
  // Instead, re-run the same quotes-policies lookup the expand action already uses,
  // with the new quote's number appended to whatever numbers that opportunity had.
  const isFirstQuoteSignal = useRef(true);
  useEffect(() => {
    if (isFirstQuoteSignal.current) { isFirstQuoteSignal.current = false; return; }
    if (!lastQuoteAdded) return;
    const opp = opportunities.find((o) => o.databaseId === lastQuoteAdded.opportunityId);
    if (!opp?.insuredDatabaseId) return;
    const existingNumbers = Array.isArray(opp.policyNumbers) ? opp.policyNumbers : [];
    const updatedNumbers = existingNumbers.includes(lastQuoteAdded.quoteNumber)
      ? existingNumbers
      : [...existingNumbers, lastQuoteAdded.quoteNumber];
    // The render's "No quotes yet." branch gates on this opportunity's own
    // policyNumbers, not on quotePolicies — without updating it here too, the
    // fetch below fills quotePolicies but the UI never reaches the branch that reads it.
    setOpportunities((prev) => prev.map((o) => (
      o.databaseId === lastQuoteAdded.opportunityId ? { ...o, policyNumbers: updatedNumbers } : o
    )));
    fetchQuotePolicies(lastQuoteAdded.opportunityId, opp.insuredDatabaseId, updatedNumbers);
    setExpanded((prev) => new Set(prev).add(lastQuoteAdded.opportunityId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteAddedSignal]);

  useEffect(() => {
    fetchOpportunities(fromDate, toDate);
  }, [fromDate, toDate, fetchOpportunities]);

  // Infinite scroll — load the next page of opportunities when the sentinel enters view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreOpportunities();
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMoreOpportunities]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dateFilterMenuRef.current && !dateFilterMenuRef.current.contains(e.target as Node)) {
        setDateFilterOpen(false);
      }
    };
    if (dateFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dateFilterOpen]);

  const getActiveDatePreset = (): DatePreset | null => {
    if (!fromDate || !toDate) return null;
    const now = new Date();
    const today = toYMD(now);
    if (toDate !== today) return null;
    if (fromDate === today) return 'today';
    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - 6);
    if (fromDate === toYMD(weekStart)) return 'week';
    const monthStart = new Date(now);
    monthStart.setDate(monthStart.getDate() - 29);
    if (fromDate === toYMD(monthStart)) return 'month';
    return null;
  };

  const applyDatePreset = (preset: DatePreset) => {
    const now = new Date();
    const to = toYMD(now);
    const start = new Date(now);
    if (preset === 'week') start.setDate(start.getDate() - 6);
    else if (preset === 'month') start.setDate(start.getDate() - 29);
    setFromDate(toYMD(start));
    setToDate(to);
    setDateFilterOpen(false);
  };

  const activeDatePreset = getActiveDatePreset();
  const dateFilterLabel = activeDatePreset === 'today' ? 'Today' : activeDatePreset === 'week' ? 'Last Week' : activeDatePreset === 'month' ? 'Last Month' : 'Date';

  return (
    <div className="sc">
      <div className="sc__inner">
        <header className="sc__head">
          <div className="sc__head-main">
            <h1 className="sc__title">Opportunities in market</h1>
          </div>

          <div className="sc__head-actions">
            <button
              type="button"
              className="mc-btn-icon"
              onClick={() => fetchOpportunities(fromDate, toDate)}
              disabled={loading}
              title="Refresh"
            >
              <FiRefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
            </button>

            <div ref={dateFilterMenuRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className={`art-filter__btn art-filter__btn--date${activeDatePreset || fromDate || toDate ? ' art-filter__btn--active' : ''}`}
                title="Filter by date"
                aria-haspopup="true"
                aria-expanded={dateFilterOpen}
                onClick={() => setDateFilterOpen((p) => !p)}
              >
                <FiCalendar size={12} />
                <span className="art-filter__btn-label">{dateFilterLabel}</span>
              </button>

              {dateFilterOpen && (
                <div className="art-filter__popover art-filter__popover--date" role="menu" aria-label="Filter by date">
                  <div className="art-filter__type-header">
                    <span className="art-filter__label">Filter by date</span>
                  </div>

                  <div className="art-filter__preset-pills">
                    {([
                      { key: 'today', label: 'Today' },
                      { key: 'week', label: 'Last Week' },
                      { key: 'month', label: 'Last Month' },
                    ] as { key: DatePreset; label: string }[]).map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        className={`art-filter__preset-pill${activeDatePreset === p.key ? ' art-filter__preset-pill--active' : ''}`}
                        onClick={() => applyDatePreset(p.key)}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  <div className="art-filter__custom-dates">
                    <div className="art-filter__date-row">
                      <label className="art-filter__label">From</label>
                      <input
                        type="date"
                        className="art-filter__date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </div>
                    <div className="art-filter__date-row">
                      <label className="art-filter__label">To</label>
                      <input
                        type="date"
                        className="art-filter__date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              className="sc-add-opp-btn"
              onClick={() => navigate(ROUTES.CENTER_SUBMISSION_ADD_OPPORTUNITY)}
            >
              <FiPlus size={12} aria-hidden="true" />
              Add Opportunity
            </button>
          </div>
        </header>

        <div className="sc__filters">
          <p className="sc__blurb">
            Every deal you are shopping, with all of its carrier submissions underneath it.
            Expand a row to see each market, where it stands, and what is holding it up.
          </p>
          <div className="sc__filter-controls">
            <select value={lobFilter} onChange={(e) => setLobFilter(e.target.value)} aria-label="Line of business">
              <option value="all">All lines of business</option>
              {lobOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select disabled value="all" aria-label="Producer or CSR" title="Producer/CSR isn't available from the live opportunities feed yet">
              <option value="all">All producers &amp; CSRs</option>
            </select>
            <select value={healthFilter} onChange={(e) => setHealthFilter(e.target.value)} aria-label="Health">
              <option value="all">Any health</option>
              {healthOptions.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort">
              <option value="effective">Sort: effective date</option>
              <option value="account">Sort: account name</option>
            </select>
          </div>
        </div>

        {loading ? (
          <SkeletonCards />
        ) : error ? (
          <div className="sc__empty">Failed to load opportunities. <button type="button" className="sc__chip" onClick={() => fetchOpportunities(fromDate, toDate)}>Retry</button></div>
        ) : opportunities.length === 0 ? (
          <div className="sc__empty">No opportunities in this window.</div>
        ) : visibleOpportunities.length === 0 ? (
          <div className="sc__empty">No opportunities match these filters.</div>
        ) : (
          <>
            <div className="sc__list">
              {visibleOpportunities.map((o, i) => {
                const oppId = o.databaseId || String(i);
                const isOpen = expanded.has(oppId);
                const quotes = Array.isArray(o.policyNumbers) ? o.policyNumbers : [];
                const bestPolicy = bestQuotePolicy(quotePolicies[oppId]?.policies);
                const bestPremium = typeof bestPolicy?.totalPremium === 'number' ? bestPolicy.totalPremium : null;
                return (
                <article className={`sc-opp${isOpen ? ' sc-opp--open' : ''}`} key={oppId}>
                  <div className="sc-opp__head">
                    <button
                      type="button"
                      className="sc-opp__caret"
                      aria-expanded={isOpen}
                      aria-label={isOpen ? 'Collapse quotes' : 'Show quotes'}
                      onClick={() => toggleExpanded(o, oppId)}
                    >
                      <FiChevronRight size={14} />
                    </button>
                    <span className="sc-opp__ident">
                      <span className="sc-opp__l1">
                        <span className="sc-opp__account">{insuredName(o)}</span>
                        <span className="sc-opp__ref" />
                      </span>
                      <span className="sc-opp__l2">
                        {lineOfBusiness(o)}
                        {assignedToLabel(o) && <><span className="sc-sep"> · </span>{assignedToLabel(o)}</>}
                      </span>
                    </span>
                    <span className="sc-opp__status">
                      {savingStageId === oppId ? (
                        <span className="sc-tag">
                          <i className="sc-tag__spinner" aria-hidden="true" />
                          Updating…
                        </span>
                      ) : editingStageId === oppId ? (
                        <span className="sc-stage-edit">
                          <AqSelect
                            value={stageLabel(o) === '—' ? '' : stageLabel(o)}
                            onChange={(v) => handleStageChange(o, oppId, v)}
                            options={stageOptions}
                            placeholder="Stage…"
                            searchable
                            defaultOpen
                            portal
                          />
                          <button
                            type="button"
                            className="sc-stage-edit__cancel"
                            onClick={() => setEditingStageId(null)}
                            aria-label="Cancel"
                          >
                            <FiX size={12} />
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="sc-tag sc-tag--editable"
                          onClick={() => o.databaseId && setEditingStageId(oppId)}
                          disabled={!o.databaseId}
                        >
                          {stageLabel(o)}
                        </button>
                      )}
                      {o.winProbability && (
                        <span className={`sc-health${winProbabilityTone(o) ? ` sc-health--${winProbabilityTone(o)}` : ''}`}>
                          <i className="sc-dot" />{o.winProbability}
                        </span>
                      )}
                      {o.databaseId && (
                        <button
                          type="button"
                          className="sc-tag sc-tag--editable"
                          onClick={() => setSelected({ kind: 'notes', opportunityId: o.databaseId!, insuredDatabaseId: o.insuredDatabaseId || '' })}
                        >
                          View Notes
                        </button>
                      )}
                    </span>
                  </div>

                  <div className="sc-opp__metrics">
                    <div className="sc-metric">
                      <div className="sc-metric__l">Effective</div>
                      <div className="sc-metric__v">{fmtDate(o.createDate)}</div>
                      <div className="sc-metric__s" />
                    </div>
                    <div className="sc-metric">
                      <div className="sc-metric__l">Target Premium</div>
                      <div className="sc-metric__v">{bestPremium != null ? fmtCurrency(bestPremium) : ''}</div>
                      <div className="sc-metric__s" />
                    </div>
                    <div className="sc-metric">
                      <div className="sc-metric__l">Best Quote</div>
                      <div className="sc-metric__v">{bestPolicy ? String(bestPolicy.number ?? '—') : ''}</div>
                      <div className="sc-metric__s">{bestPolicy ? String(bestPolicy.carrierName ?? '—') : ''}</div>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="sc-opp__quotes">
                      {quotes.length === 0 ? (
                        <p className="sc-opp__quotes-empty">No quotes yet.</p>
                      ) : quotePolicies[oppId]?.loading ? (
                        <>
                          <div className="sc-opp__quotes-head">
                            <span>Policy #</span><span>Carrier</span><span>Premium</span><span>Stage</span>
                          </div>
                          <SkeletonQuoteRows count={Math.min(quotes.length, QUOTES_PAGE_SIZE)} />
                        </>
                      ) : quotePolicies[oppId]?.error ? (
                        <p className="sc-opp__quotes-empty">Failed to load quote details.</p>
                      ) : (
                        <>
                          <div className="sc-opp__quotes-head">
                            <span>Policy #</span><span>Carrier</span><span>Premium</span><span>Stage</span>
                          </div>
                          {(quotePolicies[oppId]?.policies || []).map((p, idx) => (
                            <button
                              type="button"
                              className="sc-opp__quotes-row"
                              key={String(p.databaseId || p.number || idx)}
                              onClick={() => p.databaseId && setSelected({ kind: 'policy', policyId: String(p.databaseId), opportunityId: oppId })}
                              disabled={!p.databaseId}
                            >
                              <span className="sc-opp__quotes-num">
                                {String(p.number ?? '—')}
                                {!!bestPolicy && !!p.databaseId && p.databaseId === bestPolicy.databaseId && (
                                  <span className="sc-rec">Recommended</span>
                                )}
                              </span>
                              <span>{String(p.carrierName ?? '—')}</span>
                              <span>{typeof p.totalPremium === 'number' ? fmtCurrency(p.totalPremium) : '—'}</span>
                              <span className="sc-stage">
                                <i className="sc-dot" style={{ background: quoteStatusColor(p.isQuote) }} />
                                {quoteStatusLabel(p.isQuote)}
                              </span>
                            </button>
                          ))}
                          {quotePolicies[oppId]!.policies.length < quotePolicies[oppId]!.total && (
                            <button
                              type="button"
                              className="sc-panel__scan-btn"
                              onClick={() => o.insuredDatabaseId && handleLoadMoreQuotePolicies(oppId, o.insuredDatabaseId, quotes)}
                              disabled={quotePolicies[oppId]!.moreLoad}
                            >
                              {quotePolicies[oppId]!.moreLoad
                                ? 'Loading…'
                                : `Show more (${quotePolicies[oppId]!.policies.length} of ${quotePolicies[oppId]!.total})`}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {isOpen && (
                    <button
                      type="button"
                      className="sc-add-quote-btn"
                      onClick={() => o.databaseId && navigate(
                        ROUTES.CENTER_SUBMISSION_ADD_QUOTE.replace(':oppId', o.databaseId),
                        { state: { insuredDatabaseId: o.insuredDatabaseId, accountName: insuredName(o) } },
                      )}
                      disabled={!o.databaseId}
                    >
                      <FiPlus size={13} aria-hidden="true" />
                      Add Quote
                    </button>
                  )}

                  {isOpen && (
                    <div className="sc-opp__extra">
                      <div className="sc-opp__config">
                        <div className="sc-opp__extra-title">Configurations</div>
                        {submissionSteps[oppId]?.loading ? (
                          <SkeletonSteps />
                        ) : submissionSteps[oppId]?.error ? (
                          <p className="sc-opp__quotes-empty" style={{ padding: '8px 0' }}>Failed to load steps.</p>
                        ) : !submissionSteps[oppId]?.steps.length ? (
                          <p className="sc-opp__quotes-empty" style={{ padding: '8px 0' }}>No steps yet.</p>
                        ) : (
                          // Same checklist as AddOpportunityPage's Documents list
                          // (.sc-panel__list) — each checkbox fires check-uncheck
                          // immediately on click, no separate Save step.
                          <ul className="sc-panel__list" style={{ marginTop: 8 }}>
                            {submissionSteps[oppId]!.steps.map((s) => {
                              const isToggling = togglingStep === `${oppId}:${s.id}`;
                              return (
                              <li key={s.id} style={{ alignItems: 'center' }}>
                                <span className="sc-panel__list-icon" style={{ paddingTop: 0 }}>
                                  {isToggling ? (
                                    <i className="sc-tag__spinner" aria-hidden="true" />
                                  ) : (
                                    <input
                                      type="checkbox"
                                      className="sc-panel__list-checkbox"
                                      checked={!!s.checked}
                                      onChange={() => handleToggleStepCheck(oppId, o, s.id)}
                                      aria-label={`Mark "${s.title}" ${s.checked ? 'incomplete' : 'complete'}`}
                                    />
                                  )}
                                </span>
                                <span className="sc-panel__list-main">
                                  {s.title}
                                  <span className="sc-panel__list-meta">{s.description}</span>
                                </span>
                              </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>

                      <div className="sc-opp__insights">
                        <div className="sc-opp__extra-title sc-opp__extra-title--row">
                          <span>AI Insights</span>
                          {opportunityInsights[oppId]?.data?.currentStatus && (() => {
                            const tone = insightStatusTone(opportunityInsights[oppId]!.data!.currentStatus);
                            return (
                              <span className={`sc-health${tone ? ` sc-health--${tone}` : ''}`}>
                                <i className="sc-dot" />{opportunityInsights[oppId]!.data!.currentStatus}
                              </span>
                            );
                          })()}
                        </div>
                        {opportunityInsights[oppId]?.loading ? (
                          <p className="sc-opp__quotes-empty">Loading AI insights…</p>
                        ) : opportunityInsights[oppId]?.error ? (
                          <p className="sc-opp__quotes-empty">Failed to load AI insights.</p>
                        ) : !opportunityInsights[oppId]?.data?.insights.length && !opportunityInsights[oppId]?.data?.nextSteps.length ? (
                          <p className="sc-opp__quotes-empty">No AI insights yet for this opportunity.</p>
                        ) : (
                          <div className="sc-opp__insight-card">
                            {!!opportunityInsights[oppId]?.data?.insights.length && (
                              <>
                                <div className="sc-opp__insight-label">Insights</div>
                                <ul className="sc-opp__insight-list">
                                  {opportunityInsights[oppId]!.data!.insights.map((line, i) => <li key={i}>{line}</li>)}
                                </ul>
                              </>
                            )}
                            {!!opportunityInsights[oppId]?.data?.nextSteps.length && (
                              <>
                                <div className="sc-opp__insight-label">Next steps</div>
                                <ul className="sc-opp__insight-list">
                                  {opportunityInsights[oppId]!.data!.nextSteps.map((line, i) => <li key={i}>{line}</li>)}
                                </ul>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </article>
                );
              })}
            </div>

            {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
            {loadingMore && (
              <div className="sc-load-more">
                <FiRefreshCw size={12} className="dialer-spin" />
                Loading more…
              </div>
            )}
            {!hasMore && (
              <p className="sc__footnote">{total} of {total} opportunities loaded.</p>
            )}
          </>
        )}
      </div>
    </div>);
};
export default SubmissionCenterPage;
